<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const route = useRoute()
const api = useApi()
const toast = useToast()
const authStore = useAuthStore()

// State from route
const tempToken = computed(() => route.query.token as string || '')
const method = computed(() => (route.query.method as 'totp' | 'email') || 'totp')

// Component state
const code = ref('')
const trustDevice = ref(false)
const loading = ref(false)
const useBackupCode = ref(false)
const resendLoading = ref(false)
const resendCountdown = ref(0)

async function verify() {
  if (code.value.length < 6) {
    toast.error('Veuillez entrer un code valide')
    return
  }

  try {
    loading.value = true
    const response = await api.post('/security/2fa/verify', {
      code: code.value.replace(/\s|-/g, ''),
      method: useBackupCode.value ? 'backup_code' : method.value,
      tempToken: tempToken.value,
      trustDevice: trustDevice.value
    })

    if (response.success) {
      // Store tokens
      authStore.setAuth(response.user, response.accessToken, response.refreshToken)
      toast.success('Connexion réussie !')

      // Redirect
      const redirect = route.query.redirect as string || '/dashboard'
      router.push(redirect)
    } else {
      toast.error(response.message)
      code.value = ''
    }
  } catch (error: any) {
    toast.error(error.message || 'Code invalide')
    code.value = ''
  } finally {
    loading.value = false
  }
}

async function resendCode() {
  if (method.value !== 'email' || resendCountdown.value > 0) return

  try {
    resendLoading.value = true
    const response = await api.post('/security/2fa/send-email-code', {
      tempToken: tempToken.value,
      action: 'login'
    })

    if (response.success) {
      toast.success('Code envoyé par email')
      resendCountdown.value = 60
      startCountdown()
    } else {
      toast.error(response.message)
    }
  } catch (error: any) {
    toast.error(error.message || 'Erreur lors de l\'envoi')
  } finally {
    resendLoading.value = false
  }
}

function startCountdown() {
  const interval = setInterval(() => {
    resendCountdown.value--
    if (resendCountdown.value <= 0) {
      clearInterval(interval)
    }
  }, 1000)
}

function goBack() {
  router.push('/login')
}

onMounted(() => {
  if (!tempToken.value) {
    router.push('/login')
  }
})
</script>

<template>
  <div class="min-h-screen h-screen flex overflow-hidden">
    <!-- Left Side - Form -->
    <div class="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-12 lg:px-16 xl:px-20 bg-white dark:bg-corporate-950 overflow-y-auto py-8">
      <div class="w-full max-w-md mx-auto">
        <!-- Header with Logo (Centered) -->
        <div class="flex items-center justify-center mb-8">
          <router-link to="/" class="flex items-center justify-center">
            <img 
              src="/images/logo-icon.png" 
              alt="Dashboard" 
              class="h-12 w-auto"
            />
          </router-link>
        </div>

        <!-- Icon -->
        <div class="flex justify-center mb-4">
          <div class="w-16 h-16 rounded-full bg-corporate-100 dark:bg-corporate-800/50 flex items-center justify-center">
            <svg class="w-8 h-8 text-corporate-600 dark:text-corporate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
        </div>

        <!-- Title (Centered) -->
        <div class="mb-8 text-center">
          <h1 class="text-2xl font-semibold text-corporate-900 dark:text-white mb-2">
            Vérification en deux étapes
          </h1>
          <p class="text-corporate-500 dark:text-corporate-400">
            {{ method === 'totp' && !useBackupCode
              ? 'Entrez le code de votre application authenticator'
              : useBackupCode
                ? 'Entrez un code de secours'
                : 'Entrez le code envoyé par email'
            }}
          </p>
        </div>

        <!-- Form -->
        <form @submit.prevent="verify" class="space-y-6">
          <!-- Code Input -->
          <div>
            <label class="block text-sm font-medium text-corporate-700 dark:text-corporate-300 mb-2">
              {{ useBackupCode ? 'Code de secours' : 'Code de vérification' }}
            </label>
            <input
              v-model="code"
              :type="useBackupCode ? 'text' : 'text'"
              :placeholder="useBackupCode ? 'XXXX-XXXX' : '000000'"
              :maxlength="useBackupCode ? 9 : 6"
              class="w-full px-4 py-3 text-center text-2xl tracking-widest border border-corporate-300 dark:border-corporate-700 rounded-xl bg-white dark:bg-corporate-900 text-corporate-900 dark:text-white focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500 transition-all"
              autofocus
            />
          </div>

          <!-- Trust Device -->
          <label class="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              v-model="trustDevice"
              class="w-5 h-5 text-accent-600 border-corporate-300 dark:border-corporate-600 rounded focus:ring-accent-500"
            />
            <span class="text-sm text-corporate-600 dark:text-corporate-400">
              Faire confiance à cet appareil pendant 30 jours
            </span>
          </label>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="loading || code.length < 6"
            class="w-full h-11 flex items-center justify-center gap-2 rounded-lg border-2 border-corporate-800 bg-transparent hover:bg-corporate-800 text-corporate-800 hover:text-white dark:border-0 dark:bg-[#1E3A5F] dark:hover:bg-[#152238] dark:text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="loading" class="animate-spin h-5 w-5 border-2 border-current border-t-transparent rounded-full"></span>
            <span v-else>Vérifier</span>
          </button>
        </form>

        <!-- Divider -->
        <div class="relative my-6">
          <div class="absolute inset-0 flex items-center">
            <div class="w-full border-t border-corporate-200 dark:border-corporate-700"></div>
          </div>
          <div class="relative flex justify-center text-sm">
            <span class="px-2 bg-white dark:bg-corporate-950 text-corporate-500">ou</span>
          </div>
        </div>

        <!-- Alternative Options -->
        <div class="space-y-3">
          <!-- Resend Code (Email only) -->
          <button
            v-if="method === 'email' && !useBackupCode"
            @click="resendCode"
            :disabled="resendLoading || resendCountdown > 0"
            class="w-full py-2 text-sm text-accent-600 hover:text-accent-700 disabled:text-corporate-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <span v-if="resendLoading" class="animate-spin h-4 w-4 border-2 border-accent-600 border-t-transparent rounded-full"></span>
            <span v-else-if="resendCountdown > 0">Renvoyer le code ({{ resendCountdown }}s)</span>
            <span v-else>Renvoyer le code</span>
          </button>

          <!-- Use Backup Code -->
          <button
            @click="useBackupCode = !useBackupCode"
            class="w-full py-2 text-sm text-corporate-600 dark:text-corporate-400 hover:text-corporate-800 dark:hover:text-corporate-200"
          >
            {{ useBackupCode ? 'Utiliser le code authenticator' : 'Utiliser un code de secours' }}
          </button>

          <!-- Back to Login -->
          <button
            @click="goBack"
            class="w-full py-2 text-sm text-corporate-500 hover:text-corporate-700 dark:hover:text-corporate-300 flex items-center justify-center gap-2"
          >
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Retour à la connexion
          </button>
        </div>

        <!-- Help Text -->
        <p class="text-center text-sm text-corporate-500 dark:text-corporate-400 mt-6">
          Vous avez perdu l'accès à votre méthode 2FA ?
          <a href="mailto:support@laumeris.com" class="text-accent-600 hover:underline">
            Contactez le support
          </a>
        </p>

        <!-- Footer -->
        <p class="mt-8 text-center text-xs text-corporate-400 dark:text-corporate-500">
          &copy; {{ new Date().getFullYear() }} Laumeris. Tous droits réservés.
        </p>
      </div>
    </div>

    <!-- Right Panel - Animated Partnership (Theme Adaptive) -->
    <div class="hidden lg:flex lg:w-1/2 bg-white dark:bg-corporate-950 items-center justify-center relative overflow-hidden">
      
      <!-- Animated Background Orbs -->
      <div class="absolute inset-0 overflow-hidden">
        <div class="absolute top-1/4 left-1/4 w-96 h-96 bg-[#1E3A5F]/10 dark:bg-[#1E3A5F]/20 rounded-full blur-[120px] animate-pulse-slow" />
        <div class="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#C9B896]/5 dark:bg-[#C9B896]/10 rounded-full blur-[100px] animate-pulse-slower" />
      </div>

      <!-- Content Container -->
      <div class="relative z-10 flex flex-col items-center justify-center h-full px-12 py-16">
        
        <!-- Animated Logos -->
        <div class="flex items-center gap-6 mb-14 animate-fade-in-up" style="animation-delay: 0.2s;">
          <!-- Logo 1 -->
          <div class="relative group">
            <div class="absolute -inset-1 bg-gradient-to-r from-[#C9B896]/20 to-[#1E3A5F]/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div class="relative w-24 h-24 bg-corporate-100/50 dark:bg-white/[0.02] backdrop-blur-xl border border-corporate-200 dark:border-white/[0.08] rounded-2xl flex items-center justify-center transition-transform duration-500 hover:scale-105">
              <img src="/images/logo-icon.png" alt="Partner 1" class="h-14 w-auto" />
            </div>
          </div>

          <!-- Animated Connector -->
          <div class="flex flex-col items-center gap-1 animate-fade-in" style="animation-delay: 0.5s;">
            <div class="w-[1px] h-4 bg-gradient-to-b from-transparent to-[#C9B896]/40 dark:to-[#C9B896]/30" />
            <div class="w-3 h-3 border border-[#C9B896]/50 dark:border-[#C9B896]/40 rotate-45" />
            <div class="w-[1px] h-4 bg-gradient-to-t from-transparent to-[#C9B896]/40 dark:to-[#C9B896]/30" />
          </div>

          <!-- Logo 2 -->
          <div class="relative group">
            <div class="absolute -inset-1 bg-gradient-to-r from-[#1E3A5F]/20 to-[#C9B896]/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div class="relative w-24 h-24 bg-corporate-100/50 dark:bg-white/[0.02] backdrop-blur-xl border border-corporate-200 dark:border-white/[0.08] rounded-2xl flex items-center justify-center transition-transform duration-500 hover:scale-105">
              <img src="/images/logo-lmw.png" alt="Partner 2" class="h-16 w-auto" />
            </div>
          </div>
        </div>

        <!-- Title with reveal animation -->
        <div class="text-center mb-10 animate-fade-in-up" style="animation-delay: 0.4s;">
          <div class="inline-flex items-center gap-3 mb-6">
            <div class="w-8 h-[1px] bg-gradient-to-r from-transparent to-[#C9B896]/60 dark:to-[#C9B896]/50" />
            <span class="text-[11px] text-[#C9B896] dark:text-[#C9B896]/60 uppercase tracking-[0.4em] font-light">Partenariat</span>
            <div class="w-8 h-[1px] bg-gradient-to-l from-transparent to-[#C9B896]/60 dark:to-[#C9B896]/50" />
          </div>
          <h2 class="text-3xl font-extralight text-corporate-800 dark:text-white/90 tracking-wide mb-4">
            Plateforme de Gestion
          </h2>
          <p class="text-sm text-corporate-500 dark:text-white/40 max-w-sm leading-relaxed">
            Une alliance d'excellence au service de votre entreprise
          </p>
        </div>

        <!-- Animated Features -->
        <div class="flex gap-12 mb-14 animate-fade-in-up" style="animation-delay: 0.6s;">
          <div class="group text-center cursor-default">
            <div class="w-12 h-12 mx-auto mb-3 rounded-xl bg-corporate-100 dark:bg-white/[0.05] border border-corporate-200 dark:border-white/[0.08] flex items-center justify-center transition-all duration-300 group-hover:border-[#C9B896]/50 dark:group-hover:border-[#C9B896]/30 group-hover:shadow-[0_0_20px_rgba(201,184,150,0.15)] dark:group-hover:shadow-[0_0_20px_rgba(201,184,150,0.1)]">
              <svg class="w-5 h-5 text-corporate-400 dark:text-white/50 group-hover:text-[#C9B896] dark:group-hover:text-[#C9B896]/80 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <p class="text-[10px] text-corporate-400 dark:text-white/30 uppercase tracking-widest group-hover:text-corporate-600 dark:group-hover:text-white/50 transition-colors">Sécurisé</p>
          </div>
          <div class="group text-center cursor-default">
            <div class="w-12 h-12 mx-auto mb-3 rounded-xl bg-corporate-100 dark:bg-white/[0.05] border border-corporate-200 dark:border-white/[0.08] flex items-center justify-center transition-all duration-300 group-hover:border-[#C9B896]/50 dark:group-hover:border-[#C9B896]/30 group-hover:shadow-[0_0_20px_rgba(201,184,150,0.15)] dark:group-hover:shadow-[0_0_20px_rgba(201,184,150,0.1)]">
              <svg class="w-5 h-5 text-corporate-400 dark:text-white/50 group-hover:text-[#C9B896] dark:group-hover:text-[#C9B896]/80 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <p class="text-[10px] text-corporate-400 dark:text-white/30 uppercase tracking-widest group-hover:text-corporate-600 dark:group-hover:text-white/50 transition-colors">Performant</p>
          </div>
          <div class="group text-center cursor-default">
            <div class="w-12 h-12 mx-auto mb-3 rounded-xl bg-corporate-100 dark:bg-white/[0.05] border border-corporate-200 dark:border-white/[0.08] flex items-center justify-center transition-all duration-300 group-hover:border-[#C9B896]/50 dark:group-hover:border-[#C9B896]/30 group-hover:shadow-[0_0_20px_rgba(201,184,150,0.15)] dark:group-hover:shadow-[0_0_20px_rgba(201,184,150,0.1)]">
              <svg class="w-5 h-5 text-corporate-400 dark:text-white/50 group-hover:text-[#C9B896] dark:group-hover:text-[#C9B896]/80 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <p class="text-[10px] text-corporate-400 dark:text-white/30 uppercase tracking-widest group-hover:text-corporate-600 dark:group-hover:text-white/50 transition-colors">Innovant</p>
          </div>
        </div>

        <!-- Bottom signature -->
        <div class="text-center animate-fade-in" style="animation-delay: 0.8s;">
          <div class="inline-flex items-center gap-2">
            <div class="w-1 h-1 rounded-full bg-[#C9B896]/60 dark:bg-[#C9B896]/40" />
            <p class="text-[10px] text-corporate-400 dark:text-white/20 uppercase tracking-[0.3em] font-light">Excellence & Confiance</p>
            <div class="w-1 h-1 rounded-full bg-[#C9B896]/60 dark:bg-[#C9B896]/40" />
          </div>
        </div>
      </div>

      <!-- Corner accents -->
      <div class="absolute top-0 left-0 w-32 h-32">
        <div class="absolute top-8 left-8 w-[1px] h-16 bg-gradient-to-b from-corporate-200 dark:from-white/10 to-transparent" />
        <div class="absolute top-8 left-8 w-16 h-[1px] bg-gradient-to-r from-corporate-200 dark:from-white/10 to-transparent" />
      </div>
      <div class="absolute bottom-0 right-0 w-32 h-32">
        <div class="absolute bottom-8 right-8 w-[1px] h-16 bg-gradient-to-t from-corporate-200 dark:from-white/10 to-transparent" />
        <div class="absolute bottom-8 right-8 w-16 h-[1px] bg-gradient-to-l from-corporate-200 dark:from-white/10 to-transparent" />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Fade in up animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Simple fade in */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Slow pulse for background orbs */
@keyframes pulseSlow {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.1);
  }
}

@keyframes pulseSlower {
  0%, 100% {
    opacity: 0.2;
    transform: scale(1);
  }
  50% {
    opacity: 0.4;
    transform: scale(1.15);
  }
}

.animate-fade-in-up {
  opacity: 0;
  animation: fadeInUp 0.8s ease-out forwards;
}

.animate-fade-in {
  opacity: 0;
  animation: fadeIn 0.6s ease-out forwards;
}

.animate-pulse-slow {
  animation: pulseSlow 8s ease-in-out infinite;
}

.animate-pulse-slower {
  animation: pulseSlower 10s ease-in-out infinite;
  animation-delay: 2s;
}
</style>
